/**
 * Shared Motion Detail Stage — Golden Test implementation.
 * Permanently mounted. Open/close driven by createMotionDetailController.
 * Used by the production Work section.
 */
import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { formatIndex, type GalleryItem } from '@/lib/projects';
import { createMotionDetailController, type MotionDetailController } from './motion-detail-controller';

export interface SharedDetailHandle {
  /** Open Detail for a gallery item, originating from the clicked source element. */
  open: (item: GalleryItem, source: HTMLElement) => void;
}

interface Props {
  /** Called after the close animation fully reverses (stage hidden, cleanup done). */
  onClosed: () => void;
}

const CROSSFADE_HALF = 0.09; // 0.09s out + 0.09s in ≈ 0.18s

const SharedMotionDetailStage = forwardRef<SharedDetailHandle, Props>(({ onClosed }, ref) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const featuredRef = useRef<HTMLDivElement>(null);
  const mainImgRef = useRef<HTMLImageElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<HTMLButtonElement[]>([]);
  const detailRef = useRef<MotionDetailController | null>(null);
  const pendingSource = useRef<HTMLElement | null>(null);
  const changeToken = useRef(0);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  // item drives the rendered image list; images is the reordered array.
  const [item, setItem] = useState<GalleryItem | null>(null);
  const [images, setImages] = useState<GalleryItem['image'][]>([]);
  const [active, setActive] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Escape → same approved Close action. Listener exists only while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') detailRef.current?.close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  useEffect(() => {
    detailRef.current = createMotionDetailController({
      stage: stageRef.current!,
      backdrop: backdropRef.current!,
      featured: featuredRef.current!,
      filmstripAnchor: anchorRef.current!,
      getThumbs: () => thumbsRef.current.filter(Boolean),
      onReverseComplete: () => {
        // Keep decoded images mounted; they will be replaced on the next open.
        gsap.killTweensOf(mainImgRef.current);
        gsap.set(mainImgRef.current, { opacity: 1 });
        setIsOpen(false);
        onClosedRef.current();
      },
    });
    return () => detailRef.current?.destroy();
  }, []);

  useImperativeHandle(ref, () => ({
    open(next: GalleryItem, source: HTMLElement) {
      const all = next.project.images;
      const clicked = all[next.projectImageIndex];
      // Clicked photograph stays at index 0.
      const ordered = [clicked, ...all.filter((_, i) => i !== next.projectImageIndex)];
      pendingSource.current = source;
      thumbsRef.current = [];
      setActive(0);
      setIsOpen(true);
      setImages(ordered);
      setItem(next);
    },
  }));

  // Thumbnails mounted → build and play the timeline.
  useLayoutEffect(() => {
    if (!item || !pendingSource.current) return;
    const src = pendingSource.current;
    pendingSource.current = null;
    if (mainImgRef.current) {
      mainImgRef.current.src = images[0].src;
      mainImgRef.current.style.objectPosition = images[0].focal ?? 'center';
      gsap.set(mainImgRef.current, { opacity: 1 });
    }
    detailRef.current?.open(src);
  }, [item, images]);

  const changeMainImage = async (index: number) => {
    const img = mainImgRef.current;
    const next = images[index];
    if (!img || !next || index === active) return;
    setActive(index);
    const token = ++changeToken.current;
    const preload = new Image();
    preload.src = next.src;
    try { await preload.decode(); } catch { /* decode-safe fallback */ }
    if (token !== changeToken.current) return;
    gsap.killTweensOf(img);
    gsap.to(img, {
      opacity: 0,
      duration: CROSSFADE_HALF,
      ease: 'none',
      onComplete: () => {
        if (token !== changeToken.current) return;
        img.src = next.src;
        img.style.objectPosition = next.focal ?? 'center';
        gsap.to(img, { opacity: 1, duration: CROSSFADE_HALF, ease: 'none' });
      },
    });
  };

  return (
    <div ref={stageRef} className="shared-detail-stage">
      <div ref={backdropRef} className="shared-detail-stage__backdrop" />
      <div ref={featuredRef} className="shared-detail-stage__featured">
        <div className="shared-detail-stage__media">
        <div className="shared-detail-stage__head">
          <button
            type="button"
            className="shared-detail-stage__close"
            onClick={() => detailRef.current?.close()}
          >
            Close
          </button>
          <div className="shared-detail-stage__meta">
            <p className="font-heading text-xl font-light tracking-[-0.02em] text-foreground-950 md:text-2xl">
              {item?.project.title}
            </p>
            <p className="font-label text-[10px] uppercase tracking-[0.16em] text-foreground-600">
              {item ? `${item.project.category} · ${item.project.year}` : ''}
            </p>
          </div>
        </div>
        <div className="shared-detail-stage__surface">
          <img ref={mainImgRef} alt={item?.image.alt ?? ''} draggable={false} />
        </div>
        </div>
        {item && (
          <aside key={item.id} className="shared-detail-stage__info" aria-label="Project information">
            <p className="sd-micro">Project {formatIndex(item.project.order)}</p>
            <h3 className="mt-4 font-heading text-[28px] font-medium leading-[1.1] tracking-[-0.02em] text-foreground-950 xl:text-[34px]">{item.project.title}</h3>
            <p className="sd-desc mt-5 text-[14px] leading-relaxed text-foreground-700">{item.project.statement}</p>
            <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-6">
              <div className="col-span-2"><dt className="sd-micro">Discipline</dt><dd className="sd-val">{item.project.category}</dd></div>
              <div><dt className="sd-micro">Year</dt><dd className="sd-val">{item.project.year}</dd></div>
              <div><dt className="sd-micro">Location</dt><dd className="sd-val">{item.project.location}</dd></div>
            </dl>
            <p className="sd-micro mt-9">Credits</p>
            <dl className="mt-3 flex flex-col gap-2">
              {[
                ['Photography', item.project.credits.photography],
                ['Creative Direction', item.project.credits.creativeDirector],
                ['Styling', item.project.credits.styling],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[1fr_auto] gap-4 text-[13px]">
                  <dt className="text-foreground-600">{k}</dt>
                  <dd className="text-right text-foreground-950">{v}</dd>
                </div>
              ))}
            </dl>
          </aside>
        )}
      </div>
      <div ref={anchorRef} className="shared-detail-stage__anchor" />
      {images.map((im, i) => (
        <button
          type="button"
          key={`${item?.id ?? 'none'}-${im.src}`}
          ref={(el) => { if (el) thumbsRef.current[i] = el; }}
          className={`shared-detail-stage__thumb${i === active ? ' is-active' : ''}`}
          onClick={() => changeMainImage(i)}
          aria-label={`Show image ${i + 1}`}
        >
          <img src={im.src} style={{ objectPosition: im.focal }} alt="" draggable={false} />
        </button>
      ))}
    </div>
  );
});

SharedMotionDetailStage.displayName = 'SharedMotionDetailStage';
export default SharedMotionDetailStage;